<script setup>
import {useHandicaps} from '~/composables/useHandicaps';
import {ref, computed, watch, onMounted, onBeforeUnmount, reactive} from 'vue';
import FixtureMatrixCell from "@/components/leagues/FixtureMatrixCell";
import TeamCard from "@/components/teams/TeamCard.vue";
import {useAuthService} from "@/composables/useAuthService"
const {notify, addListener, removeListener, removeLocalListenersWithEvent} = useListenerService();

const model = defineModel();
const fullscreen = defineModel('fullscreen',{ default : () => false})
const hoverRow = ref(null);
const hoverCol = ref(null);
const {user} = useAuthService();
const props = defineProps({
  closed : {
    type: Boolean,
    default: false
  },
  simple: false,
  highlightedTeamIds: {
    type: Array,
    default: () => []
  },
  // Sport-gated, resolved by the caller — a division carries no sport of its own.
  // Off unless told.
  showSquashLevel: {
    type: Boolean,
    default: false
  },
  // The league's handicap settings (null = off): each team's chip sits beside its name in the
  // row head, as it does in the standings — the matrix is where members actually look.
  handicapSettings: {type: Object, default: null},
});
const {labelFor: handicapLabelFor, chipClassFor: handicapChipClassFor} = useHandicaps();
// the row head grows when handicap chips sit beside the names, or they spill into the grid
const rowHeadWidth = computed(() => props.handicapSettings ? 220 : 150);
const {getItem : getLcd, item : newLcd, itemPending : newLcdLoading} = useCrudApi("api/v2/leagueCycleDivisions");
onMounted(() => {
  addListener('fixtureUpdated',(fixture) => {
    if(fixture != null && fixture.leagueCycleDivisionId === model.value.id) {
      getLcd(model.value.id);
    }
  });
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
});

watch(() => newLcd, (newVal) => {
  model.value = {fullscreen : fullscreen.value, ...newVal.value};
}, { deep: true });

const currentUserTeamIds = computed(() =>
    (model.value?.teams ?? [])
        .filter(t => t.players?.map(p => p.id).includes(user.value?.playerId))
        .map(t => t.id)
);

const fixture = (homeTeam, awayTeam) => {
  const fixture = homeTeam.fixtures.find(f => f.opponentTeamId === awayTeam.id);
  if (fixture) {
    return fixture;
  }
  return {
    scoringSystem: model.value.scoringSystem,
    leagueCycleDivisionId: model.value.id,
    leagueCycleDivisionName: model.value.name,
    homeTeamId: homeTeam.id,
    awayTeamId: awayTeam.id,
  };
};

function isEqualToPrevOrNext(index) {
  const teams = model.value.teams
  const current = teams[index]
  if (!current) return false

  const prev = index > 0 ? teams[index - 1] : null
  const next = index < teams.length - 1 ? teams[index + 1] : null

  return (prev && prev.position === current.position) ||
      (next && next.position === current.position)
}

</script>
<template>
  <div :class=" fullscreen ? ' fullscreen lcd-container' : 'lcd-container'">
    <div v-if="fullscreen" @click="fullscreen = false"
         class="pa-3 font-weight-bold mb-5 text-center cursor"
         style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
      <v-icon icon="mdi-close"></v-icon>
      Close fullscreen
    </div>
    <div class="table-wrapper">
      <v-progress-linear :indeterminate="true" :active="newLcdLoading" color="primary"/>
      <v-table class="matchgrid" v-if="model.teams.length > 0">
        <template v-slot:default>
          <tr>
            <th v-if="simple" class="pos-h"></th>
            <th :class="simple ? 'member-h' : 'sticky-column corner'">
              <span v-if="!simple" class="corner-label">Member</span>
            </th>
            <th v-if="!simple" class="opp-h" v-for="(teamHeader, index) in model.teams"
                :key="'header'+teamHeader.userId"
                :class="{'cell-hover': hoverCol === index}">
              <div class="d-flex justify-center">
                <team-card :minimized="true" :show-squash-level="false" :team="teamHeader"/>
              </div>
            </th>
            <th class="tot-h tot-h--first">
              <v-tooltip location="bottom" text="Bonus points awarded for playing all games">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">Bns</span>
                </template>
              </v-tooltip>
            </th>
            <th class="sticky-column-right tot-h">Pts</th>
          </tr>
          <tr v-for="(teamRow, teamIndex) in model.teams" :key="teamRow.userId"
              :class="{
                'highlighted': highlightedTeamIds.indexOf(teamRow.id) !== -1,
                'me': currentUserTeamIds.indexOf(teamRow.id) !== -1
              }"
              @mouseover="hoverRow = teamIndex" @mouseleave="hoverRow = null">
            <td v-if="simple" style="width:42px; min-width: 42px; max-width: 42px;" class="text-center"
                :class="{'cell-hover': hoverRow === teamIndex}">
              <div class="pos-badge mx-auto">
                <span v-if="isEqualToPrevOrNext(teamIndex)">=</span>{{ teamRow.position }}
              </div>
            </td>
            <td :style="!simple ? `width:${rowHeadWidth}px; min-width: ${rowHeadWidth}px; max-width: ${rowHeadWidth}px;` : ''" class="py-2 px-1 pe-3 rowhead matrix-rowhead"
                :class="{'cell-hover': hoverRow === teamIndex, 'sticky-column': !simple}">
              <div class="d-flex align-center">
                <div v-if="!simple" class="pos-badge mr-2">
                  <span v-if="isEqualToPrevOrNext(teamIndex)">=</span>{{ teamRow.position }}
                </div>
                <div class="flex-fill rowhead-name">
                  <team-card :highlight-color="hoverCol === teamIndex ? 'success' : 'info'"
                             :highlighted="(hoverCol === teamIndex || hoverRow === teamIndex) && hoverCol !== hoverRow && hoverCol !== null"
                             :show-squash-level="showSquashLevel"
                             :handicap-label="handicapLabelFor(handicapSettings, teamRow.handicap)"
                             :handicap-tone="handicapChipClassFor(handicapSettings, teamRow.handicap)"
                             :team="teamRow"/>
                </div>
              </div>
            </td>
            <fixture-matrix-cell v-if="!simple"  v-for="(teamColumn, colIndex) in model.teams"
                                 :closed="closed"
                                 :class="{'cell-hover': hoverCol === colIndex || hoverRow === teamIndex, 'cell-hover-both' : hoverCol === colIndex && hoverRow === teamIndex}"
                                 @mouseover="hoverCol = colIndex" @mouseleave="hoverCol = null"
                                 :key="teamRow.id + '' + teamColumn.id"
                                 :league-cycle-division-id="model.id"
                                 :scoring-system="model.scoringSystem"
                                 :current-user-team-ids="currentUserTeamIds"
                                 :club-id="model.clubId"
                                 :row-team="teamRow"
                                 :column-team="teamColumn"
                                 :fixture="fixture(teamRow,teamColumn)"></fixture-matrix-cell>
            <td style="min-width: 48px; max-width: 60px; width: 48px;" class="text-center tot tot--first"
                :class="{'cell-hover': hoverRow === teamIndex}">
              <span class="bnv" :class="{'bnv--earned': teamRow.bonusPoints > 0}">{{ teamRow.bonusPoints }}</span>
            </td>
            <td style="min-width: 48px; max-width: 60px; width: 48px;"
                class="text-center sticky-column-right tot"
                :class="{'cell-hover': hoverRow === teamIndex}">
              <span class="ptv">{{ teamRow.totalPoints }}</span>
            </td>
          </tr>
        </template>
      </v-table>
      <div v-else class="pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
        No entrants in this division yet.
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

table {
  border-collapse: collapse;
  width: 100%;
}

/* hairline row separators */
.matchgrid :deep(tr + tr) {
  td, th {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.matchgrid th {
  height: 46px;
  vertical-align: middle;
  text-align: center;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.matchgrid :deep(td) {
  height: 54px;
  vertical-align: middle;
}

/* corner / member label */
.corner {
  text-align: left;
  padding-left: 12px;
}

.corner-label,
.tot-h {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.opp-h {
  min-width: 56px;
  padding: 4px;
}

.tot-h {
  min-width: 48px;
  padding: 4px;

  &.tot-h--first {
    border-left: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.tot--first {
  border-left: 1px solid rgb(var(--v-theme-borderLight));
}

/* position badge */
.pos-badge {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: rgb(var(--v-theme-light));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  flex-shrink: 0;
}

/* team rows 12px/600 */
.rowhead-name {
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
}

/* bonus + points */
.bnv {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));

  &.bnv--earned {
    color: rgb(var(--v-theme-success));
  }
}

.ptv {
  font-size: 13px;
  font-weight: 800;
  color: rgb(var(--v-theme-text));
}

.sticky-column {
  position: sticky;
  left: 0;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1;
  border-right: solid 1px rgb(var(--v-theme-borderLight));
}

.sticky-column-right {
  position: sticky;
  right: 0;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1;
  border-left: solid 1px rgb(var(--v-theme-borderLight));
}

.cell-hover {
  background-color: rgb(var(--v-theme-light));

  &.cell-hover-both {
    background-color: rgb(var(--v-theme-surface));
  }
}

/* viewer's own row + fixture-page highlighted rows pick up the brand tint */
.me,
.highlighted {
  :deep(td),
  .sticky-column,
  .sticky-column-right {
    background-color: rgb(var(--v-theme-brandTint));
  }

  .pos-badge {
    background: rgb(var(--v-theme-primary));
    color: #fff;
  }
}

.fullscreen {
  background: #fff;
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  overflow: auto;
}

/* phones: keep the row head narrow — the chip wraps under the name (TeamCard's own phone rule) */
@media (max-width: 600px) {
  .matrix-rowhead {
    width: 140px !important;
    min-width: 140px !important;
    max-width: 140px !important;
    overflow: hidden;
  }
}
</style>
